import AccountModel       from '@/models/services/account/AccountModel';
import UserModel          from '@models/account/UserModel';
import PagedCollection    from '@widesk-core/models/PagedCollection';
import { observable }     from 'mobx';
import { action }         from 'mobx';
import { runInAction }    from 'mobx';
import { makeObservable } from 'mobx';

export type AccountFailure = {
	accountId: id;
	reason: 'refused' | 'unavailable';
};

const REFUSED_STATUSES = [403, 404];

const failureReason = (error: any): AccountFailure['reason'] => {
	return REFUSED_STATUSES.includes(error?.connectorError?.response?.status) ? 'refused' : 'unavailable';
};

export class AppStore {
	public readonly user = new UserModel();

	@observable
	private _account: AccountModel | null = null;

	@observable.ref
	private _accountMapUrns: Urn[] | null = null;

	@observable.ref
	private _accountFailure: AccountFailure | null = null;

	@observable.ref
	private _pendingSelection: { accountId: id; promise: Promise<AccountFailure | null> } | null = null;

	constructor() {
		makeObservable(this);
		const localItem = localStorage.getItem('accountId');
		if (localItem) {
			this.fetchAccount(localItem);
		}
	}

	public fetchAccount(id: id): Promise<AccountFailure | null> {
		if (this._account?.id === id) return Promise.resolve(null);
		if (this._pendingSelection?.accountId === id) return this._pendingSelection.promise;

		const selection = { accountId: id, promise: this.selectAccount(id) };

		runInAction(() => this._pendingSelection = selection);

		return selection.promise;
	}

	private async selectAccount(id: id): Promise<AccountFailure | null> {
		const account = new AccountModel({ id });
		const isCurrentSelection = () => this._pendingSelection?.accountId === id;

		try {
			await account.fetch({ noError: true });
		} catch (e) {
			if (!isCurrentSelection()) return null;

			const failure: AccountFailure = { accountId: id, reason: failureReason(e) };

			runInAction(() => {
				this._pendingSelection = null;

				if (!this._account) this._accountFailure = failure;
			});

			if (failure.reason === 'refused' && localStorage.getItem('accountId') === id) {
				localStorage.removeItem('accountId');
			}

			return failure;
		}

		if (!isCurrentSelection()) return null;

		runInAction(() => {
			this._pendingSelection = null;
			this._account = account;
			this._accountMapUrns = null;
			this._accountFailure = null;
		});

		localStorage.setItem('accountId', id);

		await this.refreshAccountMapUrns();

		return null;
	}

	public async refreshAccountMapUrns() {
		const accountUrn = this.accountUrn;

		// import() dynamique : MapModel importe les stores de l'éditeur, un import statique
		// depuis appStore créerait un cycle à l'initialisation (TDZ @doc.model).
		const MapModel = (await import('@models/map/MapModel')).default;
		const collection = new PagedCollection(MapModel);

		try {
			await collection.setItemsPerPage(1000).setFilter('project.owner.accountUrn', accountUrn).list();
		} catch (e) {
			return;
		}

		if (this.accountUrn !== accountUrn) return;

		runInAction(() => this._accountMapUrns = collection.models.map(map => map.urn));
	}

	@action
	public resetAccount() {
		this._pendingSelection = null;
		this._account = null;
		this._accountMapUrns = null;
		localStorage.removeItem('accountId');
	}

	/** Urns de toutes les maps de l'account, `null` tant qu'ils ne sont pas chargés. */
	public get accountMapUrns() {
		return this._accountMapUrns;
	}

	public get accountFailure() {
		return this._accountFailure;
	}

	public get isSelectingAccount() {
		return !!this._pendingSelection;
	}

	public get account() {
		return this._account;
	}

	public get accountUrn() {
		return this.account?.urn || (this.account?.id && AccountModel.makeUrn(this.account.id)) || '';
	}

	public clear() {
		this.user.clear();
		this.resetAccount();
		runInAction(() => this._accountFailure = null);
	}
}

export default new AppStore();
